import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Save, LogOut, Plus, Trash2, ExternalLink, RotateCcw } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'offerings', label: 'Products & Packages' },
  { id: 'electronics', label: 'Electronics' },
  { id: 'nav', label: 'Navigation Tabs' },
  { id: 'email', label: 'Email Settings' },
  { id: 'hero', label: 'Hero' },
  { id: 'divisions', label: 'Divisions' },
  { id: 'whoweare', label: 'About / Mission' },
  { id: 'services', label: 'Services' },
  { id: 'whyChoose', label: 'Why Choose' },
  { id: 'industries', label: 'Industries' },
  { id: 'contact', label: 'Contact' },
];

const inputCls = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#12263f] focus:ring-1 focus:ring-[#12263f]';
const Label = ({ children }) => <label className="block text-xs font-semibold text-gray-500 mb-1">{children}</label>;

const Field = ({ label, value, onChange, textarea }) => (
  <div className="mb-3">
    <Label>{label}</Label>
    {textarea ? (
      <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={3} className={`${inputCls} resize-none`} />
    ) : (
      <input value={value || ''} onChange={(e) => onChange(e.target.value)} className={inputCls} />
    )}
  </div>
);

const AdminDashboard = ({ token, onLogout }) => {
  const { toast } = useToast();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('offerings');
  const [emailSettings, setEmailSettings] = useState(null);
  const [savingEmail, setSavingEmail] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/content`);
        setContent(data);
      } catch (e) {
        toast({ title: 'Failed to load content' });
      } finally {
        setLoading(false);
      }
      try {
        const { data } = await axios.get(`${API}/admin/email-settings`, auth);
        setEmailSettings(data);
      } catch (e) {
        // ignore; will show unavailable
      }
    })();
  }, []);

  const setEmail = (key, value) => setEmailSettings((p) => ({ ...p, [key]: value }));

  const saveEmailSettings = async () => {
    setSavingEmail(true);
    try {
      const { data } = await axios.put(`${API}/admin/email-settings`, {
        enabled: emailSettings.enabled,
        sender_email: emailSettings.sender_email,
        recipient_emails: emailSettings.recipient_emails,
        subject_prefix: emailSettings.subject_prefix,
        send_confirmation: emailSettings.send_confirmation,
      }, auth);
      setEmailSettings(data.settings);
      toast({ title: 'Email settings saved!', description: 'Inquiry emails will use these settings.' });
    } catch (e) {
      if (e?.response?.status === 401) { toast({ title: 'Session expired' }); onLogout(); }
      else toast({ title: 'Save failed' });
    } finally {
      setSavingEmail(false);
    }
  };

  const set = (path, value) => {
    setContent((prev) => {
      const next = structuredClone(prev);
      let obj = next;
      for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]];
      obj[path[path.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content`, { content }, auth);
      toast({ title: 'Saved!', description: 'Website content updated and live.' });
    } catch (e) {
      if (e?.response?.status === 401) { toast({ title: 'Session expired' }); onLogout(); }
      else toast({ title: 'Save failed', description: 'Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm('Reset ALL website content to defaults? This cannot be undone.')) return;
    try {
      const { data } = await axios.post(`${API}/admin/reset`, {}, auth);
      setContent(data.content);
      toast({ title: 'Reset complete', description: 'Content restored to defaults.' });
    } catch (e) {
      toast({ title: 'Reset failed' });
    }
  };

  if (loading || !content) {
    return <div className="min-h-screen flex items-center justify-center bg-[#f5f5f8]"><Loader2 className="animate-spin text-[#12263f]" size={32} /></div>;
  }

  // ---- generic list helpers ----
  const addItem = (key, template) => set([key], [...(content[key] || []), template]);
  const removeItem = (key, idx) => set([key], content[key].filter((_, i) => i !== idx));

  return (
    <div className="min-h-screen bg-[#f5f5f8]">
      {/* Top bar */}
      <div className="sticky top-0 z-20 bg-[#12263f] text-white">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/vetrich-logo.png" alt="Vetrich" className="h-9 w-auto" />
            <span className="font-display font-bold tracking-tight">Vetrich Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border border-white/20 hover:bg-white/10 transition-colors"><ExternalLink size={15} /> View Site</a>
            <button onClick={resetDefaults} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border border-white/20 hover:bg-white/10 transition-colors"><RotateCcw size={15} /> Reset</button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#c9a227] text-[#12263f] hover:bg-[#d8b23a] transition-colors disabled:opacity-70">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Changes
            </button>
            <button onClick={onLogout} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm hover:bg-white/10 transition-colors"><LogOut size={15} /></button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-6 grid lg:grid-cols-[220px_1fr] gap-6">
        {/* Tabs */}
        <div className="lg:sticky lg:top-20 self-start flex lg:flex-col gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`text-left px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${tab === t.id ? 'bg-[#12263f] text-white' : 'text-gray-600 hover:bg-white'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Editor */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          {tab === 'offerings' && (
            <Section title="Products & Packages" onAdd={() => addItem('offerings', { name: 'New Package', price: '₹0' })}>
              {content.offerings.map((o, i) => (
                <Row key={i} onRemove={() => removeItem('offerings', i)}>
                  <div className="grid sm:grid-cols-[1fr_140px] gap-3 flex-1">
                    <div><Label>Name</Label><input value={o.name} onChange={(e) => set(['offerings', i, 'name'], e.target.value)} className={inputCls} /></div>
                    <div><Label>Price</Label><input value={o.price} onChange={(e) => set(['offerings', i, 'price'], e.target.value)} className={inputCls} /></div>
                  </div>
                </Row>
              ))}
            </Section>
          )}

          {tab === 'electronics' && content.electronics && (
            <Section title="Electronics (Coming Soon)"
              onAdd={() => set(['electronics', 'products'], [...content.electronics.products, { name: 'Vetrich Product', image: '' }])}>
              <div className="grid sm:grid-cols-2 gap-3 mb-5">
                <Field label="Section Heading" value={content.electronics.heading} onChange={(v) => set(['electronics', 'heading'], v)} />
                <Field label="Section Subtitle" value={content.electronics.subtitle} onChange={(v) => set(['electronics', 'subtitle'], v)} />
              </div>
              {content.electronics.products.map((p, i) => (
                <Row key={i} onRemove={() => set(['electronics', 'products'], content.electronics.products.filter((_, k) => k !== i))}>
                  <div className="flex-1 flex gap-3 items-end">
                    <div className="flex-1"><Label>Product Name</Label><input value={p.name} onChange={(e) => set(['electronics', 'products', i, 'name'], e.target.value)} className={inputCls} /></div>
                    <div className="flex-1"><Label>Image URL</Label><input value={p.image} onChange={(e) => set(['electronics', 'products', i, 'image'], e.target.value)} className={inputCls} placeholder="https://..." /></div>
                    {p.image && <img src={p.image} alt="" className="w-12 h-12 rounded-lg object-cover border border-gray-200" />}
                  </div>
                </Row>
              ))}
            </Section>
          )}

          {tab === 'nav' && (
            <Section title="Navigation Tabs" onAdd={() => set(['nav'], [...(content.nav || []), { label: 'New Tab', href: '#home' }])}>
              <p className="text-xs text-gray-400 mb-3">These are the menu tabs shown in the website header. Use an in-page anchor for the link (e.g. #ayurveda, #services, #contact) or a full URL.</p>
              {(content.nav || []).map((n, i) => (
                <Row key={i} onRemove={() => set(['nav'], content.nav.filter((_, k) => k !== i))}>
                  <div className="grid sm:grid-cols-2 gap-3 flex-1">
                    <div><Label>Label</Label><input value={n.label} onChange={(e) => set(['nav', i, 'label'], e.target.value)} className={inputCls} /></div>
                    <div><Label>Link</Label><input value={n.href} onChange={(e) => set(['nav', i, 'href'], e.target.value)} className={inputCls} placeholder="#ayurveda" /></div>
                  </div>
                </Row>
              ))}
            </Section>
          )}

          {tab === 'email' && (
            <div>
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-display font-bold text-[#12263f] text-xl">Email Settings (Resend)</h2>
                <button onClick={saveEmailSettings} disabled={savingEmail || !emailSettings} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#12263f] text-white hover:bg-[#1c3a5e] transition-colors disabled:opacity-70">
                  {savingEmail ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Email Settings
                </button>
              </div>
              {!emailSettings ? (
                <p className="text-sm text-gray-500">Loading email settings…</p>
              ) : (
                <div className="space-y-4">
                  <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${emailSettings.api_key_configured ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                    <span className={`w-2 h-2 rounded-full ${emailSettings.api_key_configured ? 'bg-green-500' : 'bg-red-500'}`} />
                    Resend API key {emailSettings.api_key_configured ? 'configured' : 'NOT configured'} (stored securely in server env)
                  </div>

                  <label className="flex items-center gap-3 text-sm text-[#12263f] cursor-pointer">
                    <input type="checkbox" checked={!!emailSettings.enabled} onChange={(e) => setEmail('enabled', e.target.checked)} className="w-4 h-4" />
                    Enable email sending on inquiry submissions
                  </label>

                  <Field label="Sender (from) email — must be a Resend-verified domain, or onboarding@resend.dev for testing"
                    value={emailSettings.sender_email} onChange={(v) => setEmail('sender_email', v)} />

                  <div>
                    <Label>Recipient emails (where inquiries are sent) — comma separated</Label>
                    <input value={(emailSettings.recipient_emails || []).join(', ')}
                      onChange={(e) => setEmail('recipient_emails', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                      className={inputCls} placeholder="team@example.com, sales@example.com" />
                  </div>

                  <Field label="Subject prefix" value={emailSettings.subject_prefix} onChange={(v) => setEmail('subject_prefix', v)} />

                  <label className="flex items-center gap-3 text-sm text-[#12263f] cursor-pointer">
                    <input type="checkbox" checked={!!emailSettings.send_confirmation} onChange={(e) => setEmail('send_confirmation', e.target.checked)} className="w-4 h-4" />
                    Also send a confirmation email to the visitor
                  </label>

                  <p className="text-xs text-gray-400 leading-relaxed pt-2">
                    Note: In Resend test mode (no verified domain), emails only deliver to your own verified account address. Verify a domain in Resend to send to any recipient.
                  </p>
                </div>
              )}
            </div>
          )}

          {tab === 'hero' && (
            <Section title="Hero Section">
              <Field label="Badge" value={content.hero.badge} onChange={(v) => set(['hero', 'badge'], v)} />
              <Field label="Title" value={content.hero.title} onChange={(v) => set(['hero', 'title'], v)} />
              <Field label="Subtitle" value={content.hero.subtitle} onChange={(v) => set(['hero', 'subtitle'], v)} textarea />
              <Field label="Background Image URL" value={content.hero.image} onChange={(v) => set(['hero', 'image'], v)} />
              <div className="mt-4 flex items-center justify-between"><h3 className="font-semibold text-sm text-[#12263f]">Stats</h3>
                <button onClick={() => set(['hero', 'stats'], [...content.hero.stats, { value: '0', label: 'New Stat' }])} className="text-sm text-[#12263f] inline-flex items-center gap-1"><Plus size={14} /> Add</button></div>
              {content.hero.stats.map((s, i) => (
                <Row key={i} onRemove={() => set(['hero', 'stats'], content.hero.stats.filter((_, k) => k !== i))}>
                  <div className="grid sm:grid-cols-2 gap-3 flex-1">
                    <div><Label>Value</Label><input value={s.value} onChange={(e) => set(['hero', 'stats', i, 'value'], e.target.value)} className={inputCls} /></div>
                    <div><Label>Label</Label><input value={s.label} onChange={(e) => set(['hero', 'stats', i, 'label'], e.target.value)} className={inputCls} /></div>
                  </div>
                </Row>
              ))}
            </Section>
          )}

          {tab === 'divisions' && (
            <Section title="Divisions" onAdd={() => addItem('divisions', { icon: 'Layers', title: 'New Division', desc: '' })}>
              {content.divisions.map((d, i) => (
                <Row key={i} onRemove={() => removeItem('divisions', i)}>
                  <div className="flex-1">
                    <div className="grid sm:grid-cols-[140px_1fr] gap-3">
                      <div><Label>Icon (Monitor/Leaf/Cpu)</Label><input value={d.icon} onChange={(e) => set(['divisions', i, 'icon'], e.target.value)} className={inputCls} /></div>
                      <div><Label>Title</Label><input value={d.title} onChange={(e) => set(['divisions', i, 'title'], e.target.value)} className={inputCls} /></div>
                    </div>
                    <div className="mt-3"><Label>Description</Label><textarea value={d.desc} onChange={(e) => set(['divisions', i, 'desc'], e.target.value)} rows={2} className={`${inputCls} resize-none`} /></div>
                  </div>
                </Row>
              ))}
            </Section>
          )}

          {tab === 'whoweare' && (
            <Section title="About / Mission / Vision">
              <Field label="Heading" value={content.whoweare.heading} onChange={(v) => set(['whoweare', 'heading'], v)} />
              <Field label="Paragraph" value={content.whoweare.paragraph} onChange={(v) => set(['whoweare', 'paragraph'], v)} textarea />
              <div className="grid sm:grid-cols-2 gap-4 mt-2">
                <div className="border border-gray-100 rounded-lg p-3">
                  <Field label="Mission Title" value={content.whoweare.mission.title} onChange={(v) => set(['whoweare', 'mission', 'title'], v)} />
                  <Field label="Mission Text" value={content.whoweare.mission.desc} onChange={(v) => set(['whoweare', 'mission', 'desc'], v)} textarea />
                </div>
                <div className="border border-gray-100 rounded-lg p-3">
                  <Field label="Vision Title" value={content.whoweare.vision.title} onChange={(v) => set(['whoweare', 'vision', 'title'], v)} />
                  <Field label="Vision Text" value={content.whoweare.vision.desc} onChange={(v) => set(['whoweare', 'vision', 'desc'], v)} textarea />
                </div>
              </div>
            </Section>
          )}

          {tab === 'services' && (
            <Section title="Services" onAdd={() => addItem('services', { num: String(content.services.length + 1).padStart(2, '0'), title: 'New Service', desc: '' })}>
              {content.services.map((s, i) => (
                <Row key={i} onRemove={() => removeItem('services', i)}>
                  <div className="flex-1">
                    <div className="grid sm:grid-cols-[80px_1fr] gap-3">
                      <div><Label>No.</Label><input value={s.num} onChange={(e) => set(['services', i, 'num'], e.target.value)} className={inputCls} /></div>
                      <div><Label>Title</Label><input value={s.title} onChange={(e) => set(['services', i, 'title'], e.target.value)} className={inputCls} /></div>
                    </div>
                    <div className="mt-3"><Label>Description</Label><textarea value={s.desc} onChange={(e) => set(['services', i, 'desc'], e.target.value)} rows={2} className={`${inputCls} resize-none`} /></div>
                  </div>
                </Row>
              ))}
            </Section>
          )}

          {tab === 'whyChoose' && (
            <Section title="Why Choose Us" onAdd={() => set(['whyChoose'], [...content.whyChoose, 'New reason'])}>
              {content.whyChoose.map((w, i) => (
                <Row key={i} onRemove={() => set(['whyChoose'], content.whyChoose.filter((_, k) => k !== i))}>
                  <input value={w} onChange={(e) => set(['whyChoose', i], e.target.value)} className={`${inputCls} flex-1`} />
                </Row>
              ))}
            </Section>
          )}

          {tab === 'industries' && (
            <Section title="Industries We Serve" onAdd={() => set(['industries'], [...content.industries, 'New Industry'])}>
              {content.industries.map((w, i) => (
                <Row key={i} onRemove={() => set(['industries'], content.industries.filter((_, k) => k !== i))}>
                  <input value={w} onChange={(e) => set(['industries', i], e.target.value)} className={`${inputCls} flex-1`} />
                </Row>
              ))}
            </Section>
          )}

          {tab === 'contact' && (
            <Section title="Contact Information">
              <Field label="Phone" value={content.contact.phone} onChange={(v) => set(['contact', 'phone'], v)} />
              <Field label="Email" value={content.contact.email} onChange={(v) => set(['contact', 'email'], v)} />
              <Field label="Location" value={content.contact.location} onChange={(v) => set(['contact', 'location'], v)} />
              <Field label="WhatsApp Link" value={content.contact.whatsapp} onChange={(v) => set(['contact', 'whatsapp'], v)} />
            </Section>
          )}
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, onAdd, children }) => (
  <div>
    <div className="flex items-center justify-between mb-5">
      <h2 className="font-display font-bold text-[#12263f] text-xl">{title}</h2>
      {onAdd && <button onClick={onAdd} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-[#12263f] text-white hover:bg-[#1c3a5e] transition-colors"><Plus size={15} /> Add</button>}
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

const Row = ({ children, onRemove }) => (
  <div className="flex items-start gap-3 bg-[#f8f9fb] border border-gray-100 rounded-xl p-3">
    {children}
    <button onClick={onRemove} className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-red-500 hover:bg-red-50 transition-colors" title="Remove"><Trash2 size={16} /></button>
  </div>
);

export default AdminDashboard;
